"use client";

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

type CropRect = { x: number; y: number; w: number; h: number };
type CropHandle = "move" | "n" | "ne" | "e" | "se" | "s" | "sw" | "w" | "nw";

export type ImageCropSelection = {
  rect: CropRect;
  initialRect: CropRect;
  natural: { w: number; h: number };
  frameMm: { w: number; h: number; offsetX: number; offsetY: number };
};

export type ImageCropApplyPatch = {
  scale: number;
  pan_mm: { x: number; y: number };
  crop: ImageCropSelection;
};

const CROP_HANDLES: Array<{ id: Exclude<CropHandle, "move">; className: string; cursor: string }> = [
  { id: "nw", className: "left-0 top-0 -translate-x-1/2 -translate-y-1/2", cursor: "nwse-resize" },
  { id: "n", className: "left-1/2 top-0 -translate-x-1/2 -translate-y-1/2", cursor: "ns-resize" },
  { id: "ne", className: "right-0 top-0 translate-x-1/2 -translate-y-1/2", cursor: "nesw-resize" },
  { id: "e", className: "right-0 top-1/2 translate-x-1/2 -translate-y-1/2", cursor: "ew-resize" },
  { id: "se", className: "right-0 bottom-0 translate-x-1/2 translate-y-1/2", cursor: "nwse-resize" },
  { id: "s", className: "left-1/2 bottom-0 -translate-x-1/2 translate-y-1/2", cursor: "ns-resize" },
  { id: "sw", className: "left-0 bottom-0 -translate-x-1/2 translate-y-1/2", cursor: "nesw-resize" },
  { id: "w", className: "left-0 top-1/2 -translate-x-1/2 -translate-y-1/2", cursor: "ew-resize" },
];

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function getLargestAspectRect(bounds: { w: number; h: number }, aspect: number): CropRect {
  const safeAspect = Math.max(0.05, aspect);
  let w = bounds.w;
  let h = w / safeAspect;

  if (h > bounds.h) {
    h = bounds.h;
    w = h * safeAspect;
  }

  return {
    x: (bounds.w - w) / 2,
    y: (bounds.h - h) / 2,
    w,
    h,
  };
}

function constrainCropRect(rect: CropRect, bounds: { w: number; h: number }): CropRect {
  const minW = Math.min(bounds.w, Math.max(24, bounds.w * 0.04));
  const minH = Math.min(bounds.h, Math.max(24, bounds.h * 0.04));
  const w = clamp(Number(rect.w) || bounds.w, minW, bounds.w);
  const h = clamp(Number(rect.h) || bounds.h, minH, bounds.h);
  const x = clamp(Number(rect.x) || 0, 0, Math.max(0, bounds.w - w));
  const y = clamp(Number(rect.y) || 0, 0, Math.max(0, bounds.h - h));

  return { x, y, w, h };
}

function cropRectFromElement(args: {
  natural: { w: number; h: number };
  frameWmm: number;
  frameHmm: number;
  initialScale: number;
  initialPanMm: { x: number; y: number };
}) {
  const { natural, frameWmm, frameHmm, initialPanMm } = args;
  const aspect = Math.max(0.05, frameWmm / Math.max(0.05, frameHmm));
  const baseScale = Math.max(frameWmm / Math.max(1, natural.w), frameHmm / Math.max(1, natural.h));
  const finalScale = baseScale * Math.max(0.0001, Number(args.initialScale || 1));

  if (!Number.isFinite(finalScale) || finalScale <= 0) {
    return getLargestAspectRect(natural, aspect);
  }

  const w = frameWmm / finalScale;
  const h = frameHmm / finalScale;
  if (w <= 0 || h <= 0 || w > natural.w * 1.01 || h > natural.h * 1.01) {
    return getLargestAspectRect(natural, aspect);
  }

  const centerX = natural.w / 2 - Number(initialPanMm?.x || 0) / finalScale;
  const centerY = natural.h / 2 - Number(initialPanMm?.y || 0) / finalScale;

  return constrainCropRect(
    {
      x: centerX - w / 2,
      y: centerY - h / 2,
      w,
      h,
    },
    natural
  );
}

function resizeCropRect(args: {
  rect: CropRect;
  handle: CropHandle;
  deltaX: number;
  deltaY: number;
  bounds: { w: number; h: number };
}) {
  const { rect, handle, deltaX, deltaY, bounds } = args;
  let left = rect.x;
  let right = rect.x + rect.w;
  let top = rect.y;
  let bottom = rect.y + rect.h;
  const minW = Math.min(bounds.w, Math.max(24, bounds.w * 0.04));
  const minH = Math.min(bounds.h, Math.max(24, bounds.h * 0.04));

  if (handle === "move") {
    return constrainCropRect({ ...rect, x: rect.x + deltaX, y: rect.y + deltaY }, bounds);
  }

  if (handle.includes("w")) left += deltaX;
  if (handle.includes("e")) right += deltaX;
  if (handle.includes("n")) top += deltaY;
  if (handle.includes("s")) bottom += deltaY;

  left = clamp(left, 0, bounds.w - minW);
  right = clamp(right, minW, bounds.w);
  top = clamp(top, 0, bounds.h - minH);
  bottom = clamp(bottom, minH, bounds.h);

  if (right - left < minW) {
    if (handle.includes("w")) left = right - minW;
    else right = left + minW;
  }
  if (bottom - top < minH) {
    if (handle.includes("n")) top = bottom - minH;
    else bottom = top + minH;
  }

  return constrainCropRect({ x: left, y: top, w: right - left, h: bottom - top }, bounds);
}

export function buildImageCropElementPatch(element: any, patch: ImageCropApplyPatch) {
  const frame = patch.crop.frameMm;
  const rotateDeg = Number(element?.rotate_deg || 0);
  const oldX = Number(element?.x_mm || 0);
  const oldY = Number(element?.y_mm || 0);
  const oldW = Math.max(1, Number(element?.w_mm || frame.w));
  const oldH = Math.max(1, Number(element?.h_mm || frame.h));
  const nextW = Math.max(1, frame.w);
  const nextH = Math.max(1, frame.h);
  const rad = (rotateDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);

  const oldCenter = { x: oldW / 2, y: oldH / 2 };
  const newCenter = { x: nextW / 2, y: nextH / 2 };
  const oldLocal = {
    x: oldCenter.x + (frame.offsetX - oldCenter.x) * cos - (frame.offsetY - oldCenter.y) * sin,
    y: oldCenter.y + (frame.offsetX - oldCenter.x) * sin + (frame.offsetY - oldCenter.y) * cos,
  };
  const newLocal = {
    x: newCenter.x + (0 - newCenter.x) * cos - (0 - newCenter.y) * sin,
    y: newCenter.y + (0 - newCenter.x) * sin + (0 - newCenter.y) * cos,
  };

  return {
    image_mode: "frame",
    fit: "cover",
    x_mm: Number((oldX + oldLocal.x - newLocal.x).toFixed(3)),
    y_mm: Number((oldY + oldLocal.y - newLocal.y).toFixed(3)),
    w_mm: Number(nextW.toFixed(3)),
    h_mm: Number(nextH.toFixed(3)),
    scale: patch.scale,
    pan_mm: patch.pan_mm,
  };
}

export default function ImageCropModal({
  imageUrl,
  frameWmm,
  frameHmm,
  initialScale,
  initialPanMm,
  labels,
  onApply,
  onClose,
}: {
  imageUrl: string;
  frameWmm: number;
  frameHmm: number;
  initialScale: number;
  initialPanMm: { x: number; y: number };
  labels: {
    title: string;
    apply: string;
    cancel: string;
    hint: string;
  };
  onApply: (patch: ImageCropApplyPatch) => void;
  onClose: () => void;
}) {
  const [natural, setNatural] = useState({ w: 0, h: 0 });
  const [viewport, setViewport] = useState({ w: 760, h: 520 });
  const [cropRect, setCropRect] = useState<CropRect | null>(null);
  const initialCropRef = useRef<CropRect | null>(null);
  const dragRef = useRef<null | { handle: CropHandle; startX: number; startY: number; startRect: CropRect }>(null);

  useEffect(() => {
    function updateViewport() {
      setViewport({
        w: Math.max(260, Math.min(860, window.innerWidth - 96)),
        h: Math.max(260, Math.min(620, window.innerHeight - 260)),
      });
    }

    updateViewport();
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  useEffect(() => {
    if (natural.w <= 0 || natural.h <= 0) return;
    if (cropRect) return;
    const initial = cropRectFromElement({
      natural,
      frameWmm: Math.max(1, frameWmm),
      frameHmm: Math.max(1, frameHmm),
      initialScale,
      initialPanMm,
    });
    initialCropRef.current = initial;
    setCropRect(initial);
  }, [cropRect, frameHmm, frameWmm, initialPanMm, initialScale, natural]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "Enter") applyCrop();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, cropRect]);

  const imageAspect = natural.w > 0 && natural.h > 0 ? natural.w / natural.h : 1;
  let displayW = viewport.w;
  let displayH = displayW / imageAspect;
  if (displayH > viewport.h) {
    displayH = viewport.h;
    displayW = displayH * imageAspect;
  }
  const displayScale = natural.w > 0 ? displayW / natural.w : 1;

  useEffect(() => {
    const onMove = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || !cropRect || natural.w <= 0 || natural.h <= 0 || displayScale <= 0) return;

      event.preventDefault();
      const deltaX = (event.clientX - drag.startX) / displayScale;
      const deltaY = (event.clientY - drag.startY) / displayScale;
      setCropRect(
        resizeCropRect({
          rect: drag.startRect,
          handle: drag.handle,
          deltaX,
          deltaY,
          bounds: natural,
        })
      );
    };

    const onUp = () => {
      dragRef.current = null;
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [cropRect, displayScale, natural]);

  function beginCropDrag(event: React.PointerEvent, handle: CropHandle) {
    if (!cropRect) return;
    event.preventDefault();
    event.stopPropagation();
    dragRef.current = {
      handle,
      startX: event.clientX,
      startY: event.clientY,
      startRect: cropRect,
    };
  }

  function applyCrop() {
    const initialRect = initialCropRef.current;
    if (!cropRect || !initialRect || natural.w <= 0 || natural.h <= 0) return;

    const nextFrameWmm = Math.max(1, frameWmm * (cropRect.w / Math.max(1, initialRect.w)));
    const nextFrameHmm = Math.max(1, frameHmm * (cropRect.h / Math.max(1, initialRect.h)));
    const offsetXmm = (cropRect.x - initialRect.x) * (frameWmm / Math.max(1, initialRect.w));
    const offsetYmm = (cropRect.y - initialRect.y) * (frameHmm / Math.max(1, initialRect.h));
    const baseScale = Math.max(nextFrameWmm / Math.max(1, natural.w), nextFrameHmm / Math.max(1, natural.h));
    const finalScaleX = nextFrameWmm / Math.max(1, cropRect.w);
    const finalScaleY = nextFrameHmm / Math.max(1, cropRect.h);
    const finalScale = (finalScaleX + finalScaleY) / 2;
    const cropCenterX = cropRect.x + cropRect.w / 2;
    const cropCenterY = cropRect.y + cropRect.h / 2;

    onApply({
      scale: Number((finalScale / Math.max(0.0001, baseScale)).toFixed(4)),
      pan_mm: {
        x: Number(((natural.w / 2 - cropCenterX) * finalScale).toFixed(3)),
        y: Number(((natural.h / 2 - cropCenterY) * finalScale).toFixed(3)),
      },
      crop: {
        rect: cropRect,
        initialRect,
        natural,
        frameMm: {
          w: nextFrameWmm,
          h: nextFrameHmm,
          offsetX: offsetXmm,
          offsetY: offsetYmm,
        },
      },
    });
  }

  const cropStyle = cropRect
    ? {
        left: cropRect.x * displayScale,
        top: cropRect.y * displayScale,
        width: cropRect.w * displayScale,
        height: cropRect.h * displayScale,
      }
    : null;

  const modal =
    typeof document !== "undefined"
      ? createPortal(
          <div className="fixed inset-0 z-[22000] flex items-center justify-center bg-black/70 p-4 text-[#272727]">
            <div className="relative flex max-h-[92dvh] w-full max-w-[860px] flex-col rounded-xl bg-white shadow-2xl">
              <div className="flex items-center justify-between px-5 py-4">
                <div className="text-lg font-bold">{labels.title}</div>
                <button
                  type="button"
                  className="rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                  onClick={onClose}
                  aria-label={labels.cancel}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="m6 6 12 12M18 6 6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                  </svg>
                </button>
              </div>

              <div className="flex min-h-0 flex-1 flex-col items-center gap-4 overflow-auto px-5 pb-5">
                <div
                  className="relative bg-slate-950 shadow-xl"
                  style={{ width: displayW, height: displayH, touchAction: "none" }}
                >
                  <img
                    src={imageUrl}
                    alt=""
                    draggable={false}
                    onLoad={(event) => {
                      const img = event.currentTarget;
                      setNatural({ w: img.naturalWidth || img.width || 1, h: img.naturalHeight || img.height || 1 });
                    }}
                    className="pointer-events-none absolute inset-0 select-none"
                    style={{
                      width: displayW,
                      height: displayH,
                    }}
                  />

                  {cropStyle ? (
                    <>
                      <div className="pointer-events-none absolute left-0 right-0 top-0 bg-black/55" style={{ height: cropStyle.top }} />
                      <div
                        className="pointer-events-none absolute bottom-0 left-0 right-0 bg-black/55"
                        style={{ top: cropStyle.top + cropStyle.height }}
                      />
                      <div
                        className="pointer-events-none absolute bg-black/55"
                        style={{
                          left: 0,
                          top: cropStyle.top,
                          width: cropStyle.left,
                          height: cropStyle.height,
                        }}
                      />
                      <div
                        className="pointer-events-none absolute bg-black/55"
                        style={{
                          left: cropStyle.left + cropStyle.width,
                          top: cropStyle.top,
                          right: 0,
                          height: cropStyle.height,
                        }}
                      />
                      <div
                        className="absolute border-2 border-[#69cbd0]"
                        style={{ ...cropStyle, cursor: "move" }}
                        onPointerDown={(event) => beginCropDrag(event, "move")}
                      >
                        <div className="pointer-events-none absolute inset-0 border border-dashed border-white/70" />
                        {CROP_HANDLES.map((handle) => (
                          <button
                            key={handle.id}
                            type="button"
                            aria-label={`${labels.title}: ${handle.id}`}
                            className={`absolute h-4 w-4 rounded-sm border border-white bg-[#69cbd0] shadow ${handle.className}`}
                            style={{ cursor: handle.cursor }}
                            onPointerDown={(event) => beginCropDrag(event, handle.id)}
                          />
                        ))}
                      </div>
                    </>
                  ) : null}
                </div>

                <div className="text-center text-xs text-slate-500">{labels.hint}</div>

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    className="rounded-lg border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    onClick={onClose}
                  >
                    {labels.cancel}
                  </button>
                  <button
                    type="button"
                    className="editor-accent-button px-7 py-2 text-sm"
                    onClick={applyCrop}
                  >
                    {labels.apply}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )
      : null;

  return modal;
}
